// Automated Lottery Service - 20 addresses, auto-play every 30 minutes
export interface AutoLotteryPlayer {
  address: string;
  name: string;
  avatar: string;
  winCount: number;
  totalParticipations: number;
}

export interface AutoLotteryRound {
  id: number;
  startTime: number;
  endTime: number;
  participants: AutoLotteryPlayer[];
  winner: AutoLotteryPlayer | null;
  prizePool: number;
  status: 'active' | 'ended' | 'pending';
}

class AutoLotteryService {
  private players: AutoLotteryPlayer[] = [
    { address: '0x1234...5678', name: 'CryptoWhale', avatar: '🐋', winCount: 3, totalParticipations: 15 },
    { address: '0x2345...6789', name: 'DiamondHands', avatar: '💎', winCount: 1, totalParticipations: 12 },
    { address: '0x3456...789A', name: 'MoonRider', avatar: '🚀', winCount: 2, totalParticipations: 18 },
    { address: '0x4567...89AB', name: 'DegenTrader', avatar: '🎯', winCount: 0, totalParticipations: 8 },
    { address: '0x5678...9ABC', name: 'HODLMaster', avatar: '⚡', winCount: 4, totalParticipations: 20 },
    { address: '0x6789...ABCD', name: 'YieldFarmer', avatar: '🌾', winCount: 1, totalParticipations: 14 },
    { address: '0x789A...BCDE', name: 'NFTCollector', avatar: '🖼️', winCount: 2, totalParticipations: 16 },
    { address: '0x89AB...CDEF', name: 'AlphaHunter', avatar: '🦁', winCount: 3, totalParticipations: 19 },
    { address: '0x9ABC...DEF0', name: 'GigaBrain', avatar: '🧠', winCount: 5, totalParticipations: 22 },
    { address: '0xABCD...EF01', name: 'RektResistant', avatar: '🛡️', winCount: 1, totalParticipations: 11 },
    { address: '0xBCDE...F012', name: 'PumpChaser', avatar: '📈', winCount: 0, totalParticipations: 7 },
    { address: '0xCDEF...0123', name: 'DipBuyer', avatar: '📉', winCount: 2, totalParticipations: 13 },
    { address: '0xDEF0...1234', name: 'FlashLoan', avatar: '⚡', winCount: 1, totalParticipations: 9 },
    { address: '0xEF01...2345', name: 'Arbitrageur', avatar: '🔄', winCount: 3, totalParticipations: 17 },
    { address: '0xF012...3456', name: 'LiquidityPool', avatar: '🌊', winCount: 2, totalParticipations: 15 },
    { address: '0x0123...4567', name: 'SmartContract', avatar: '🤖', winCount: 4, totalParticipations: 21 },
    { address: '0x1234...5670', name: 'GasOptimizer', avatar: '⛽', winCount: 1, totalParticipations: 10 },
    { address: '0x2345...6701', name: 'MEVBot', avatar: '🤖', winCount: 6, totalParticipations: 25 },
    { address: '0x3456...7012', name: 'StakingReward', avatar: '🏆', winCount: 2, totalParticipations: 14 },
    { address: '0x4567...8023', name: 'YieldMaxi', avatar: '💰', winCount: 3, totalParticipations: 18 }
  ];

  private currentRound: AutoLotteryRound | null = null;
  private roundHistory: AutoLotteryRound[] = [];
  private roundCounter = 1;
  private intervalId: NodeJS.Timeout | null = null;
  private callbacks: ((round: AutoLotteryRound) => void)[] = [];

  constructor() {
    this.loadFromStorage();
    this.startAutoLottery();
  }

  private loadFromStorage() {
    try {
      const savedHistory = localStorage.getItem('copycat_lottery_history');
      const savedPlayers = localStorage.getItem('copycat_lottery_players');
      const savedCounter = localStorage.getItem('copycat_lottery_counter');

      if (savedHistory) {
        this.roundHistory = JSON.parse(savedHistory);
      }
      if (savedPlayers) {
        this.players = JSON.parse(savedPlayers);
      }
      if (savedCounter) {
        this.roundCounter = parseInt(savedCounter);
      }
    } catch (error) {
      console.error('Failed to load lottery data from storage:', error);
    }
  }

  private saveToStorage() {
    try {
      localStorage.setItem('copycat_lottery_history', JSON.stringify(this.roundHistory));
      localStorage.setItem('copycat_lottery_players', JSON.stringify(this.players));
      localStorage.setItem('copycat_lottery_counter', this.roundCounter.toString());
    } catch (error) {
      console.error('Failed to save lottery data to storage:', error);
    }
  }

  private startAutoLottery() {
    // Start first round immediately
    this.startNewRound();

    // Set interval for every 30 minutes (1800000 ms)
    this.intervalId = setInterval(() => {
      this.endCurrentRound();
      setTimeout(() => this.startNewRound(), 2000); // 2 second delay for dramatic effect
    }, 30 * 60 * 1000); // 30 minutes

    console.log('🎰 Auto Lottery started - New round every 30 minutes');
  }

  private startNewRound() {
    const now = Date.now();
    const endTime = now + (30 * 60 * 1000); // 30 minutes from now

    // Randomly select 8-15 participants from the 20 players
    const participantCount = Math.floor(Math.random() * 8) + 8; // 8-15 participants
    const shuffled = [...this.players].sort(() => Math.random() - 0.5);
    const participants = shuffled.slice(0, participantCount);

    // Update participation counts
    participants.forEach(player => {
      const playerIndex = this.players.findIndex(p => p.address === player.address);
      if (playerIndex !== -1) {
        this.players[playerIndex].totalParticipations++;
      }
    });

    this.currentRound = {
      id: this.roundCounter++,
      startTime: now,
      endTime: endTime,
      participants: participants,
      winner: null,
      prizePool: participantCount * 0.01, // 0.01 ETH per participant
      status: 'active'
    };

    console.log(`🎰 New lottery round #${this.currentRound.id} started with ${participantCount} participants`);
    this.notifyCallbacks(this.currentRound);
    this.saveToStorage();
  }

  private endCurrentRound() {
    if (!this.currentRound || this.currentRound.status !== 'active') return;

    // Select random winner
    const randomIndex = Math.floor(Math.random() * this.currentRound.participants.length);
    const winner = this.currentRound.participants[randomIndex];

    // Update winner's win count
    const winnerIndex = this.players.findIndex(p => p.address === winner.address);
    if (winnerIndex !== -1) {
      this.players[winnerIndex].winCount++;
    }

    this.currentRound.winner = winner;
    this.currentRound.status = 'ended';

    // Add to history
    this.roundHistory.unshift({ ...this.currentRound });
    
    // Keep only last 50 rounds in history
    if (this.roundHistory.length > 50) {
      this.roundHistory = this.roundHistory.slice(0, 50);
    }

    console.log(`🏆 Lottery round #${this.currentRound.id} ended! Winner: ${winner.name} (${winner.address})`);
    this.notifyCallbacks(this.currentRound);
    this.saveToStorage();
  }

  // Public methods
  getCurrentRound(): AutoLotteryRound | null {
    return this.currentRound;
  }

  getRoundHistory(): AutoLotteryRound[] {
    return this.roundHistory;
  }

  getPlayers(): AutoLotteryPlayer[] {
    return [...this.players].sort((a, b) => b.winCount - a.winCount); // Sort by win count
  }

  getTopWinners(limit: number = 5): AutoLotteryPlayer[] {
    return this.getPlayers().slice(0, limit);
  }

  getTimeUntilNextRound(): number {
    if (!this.currentRound) return 0;
    return Math.max(0, this.currentRound.endTime - Date.now());
  }

  getStats() {
    const totalRounds = this.roundHistory.length + (this.currentRound ? 1 : 0);
    const totalPrizeDistributed = this.roundHistory.reduce((sum, round) => sum + round.prizePool, 0);
    const totalParticipations = this.players.reduce((sum, player) => sum + player.totalParticipations, 0);

    return {
      totalRounds,
      totalPrizeDistributed,
      totalParticipations,
      activePlayers: this.players.length,
      currentRoundId: this.currentRound?.id || 0
    };
  }

  // Event system
  onRoundUpdate(callback: (round: AutoLotteryRound) => void) {
    this.callbacks.push(callback);
  }

  private notifyCallbacks(round: AutoLotteryRound) {
    this.callbacks.forEach(callback => {
      try {
        callback(round);
      } catch (error) {
        console.error('Error in lottery callback:', error);
      }
    });
  }

  // Manual controls (for testing)
  forceEndRound() {
    if (this.currentRound && this.currentRound.status === 'active') {
      this.endCurrentRound();
      setTimeout(() => this.startNewRound(), 2000);
    }
  }

  // Cleanup
  destroy() {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
  }
}

// Singleton instance
export const autoLotteryService = new AutoLotteryService();
export default autoLotteryService;
